/**
 * ============================================================================
 * Iron Meridian - Connection Status Badge Component
 * ============================================================================
 * 
 * Visual indicator for current connection/persistence status.
 * 
 * COLORS:
 * - Local: Blue - localStorage fallback
 * - Connected: Green - D1 database active  
 * - Online: Yellow/Gold - Multiplayer mode (future)
 * - Offline: Red - Error/no connection
 * 
 * ============================================================================
 */

import { useConnectionStatus } from '../hooks/useConnectionStatus.js'
import { CONNECTION_STATUS } from '../services/connectionStatus.js'

// ============================================================================
// BADGE CONFIGURATION
// ============================================================================

const BADGE_CONFIG = {
  [CONNECTION_STATUS.LOCAL]: {
    label: 'Local',
    bgColor: 'bg-blue-500/20',
    borderColor: 'border-blue-500/30',
    dotColor: 'bg-blue-500',
    textColor: 'text-blue-400',
    animate: false
  },
  [CONNECTION_STATUS.CONNECTED]: {
    label: 'Connected',
    bgColor: 'bg-green-500/20',
    borderColor: 'border-green-500/30',
    dotColor: 'bg-green-500',
    textColor: 'text-green-400',
    animate: true
  },
  [CONNECTION_STATUS.ONLINE]: {
    label: 'Online',
    bgColor: 'bg-yellow-500/20',
    borderColor: 'border-yellow-500/30',
    dotColor: 'bg-yellow-500',
    textColor: 'text-yellow-400',
    animate: true
  },
  [CONNECTION_STATUS.OFFLINE]: {
    label: 'Offline',
    bgColor: 'bg-red-500/20',
    borderColor: 'border-red-500/30',
    dotColor: 'bg-red-500',
    textColor: 'text-red-400',
    animate: true
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Connection status badge with color-coded indicator
 * @param {Object} props
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element}
 */
export function ConnectionStatusBadge({ className = '' }) {
  const { status } = useConnectionStatus()
  const config = BADGE_CONFIG[status] || BADGE_CONFIG[CONNECTION_STATUS.LOCAL]

  return (
    <div 
      className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border ${config.bgColor} ${config.borderColor} ${className}`}
      title={getTooltip(status)}
    >
      <div 
        className={`w-2 h-2 rounded-full ${config.dotColor} ${config.animate ? 'animate-pulse' : ''}`}
      />
      <span className={`text-xs font-medium ${config.textColor}`}>
        {config.label}
      </span>
    </div>
  )
}

/**
 * Get tooltip text for status
 */
function getTooltip(status) {
  switch (status) {
    case CONNECTION_STATUS.LOCAL:
      return 'Game data saved to browser storage'
    case CONNECTION_STATUS.CONNECTED:
      return 'Connected to cloud database'
    case CONNECTION_STATUS.ONLINE:
      return 'Multiplayer mode - connected to game server'
    case CONNECTION_STATUS.OFFLINE:
      return 'Connection lost - changes may not be saved'
    default:
      return 'Unknown connection status'
  }
}

export default ConnectionStatusBadge
